/**
 * CONSECUENCIAS SECTION — Rediseño B2B SaaS
 *
 * Consistencia con sistema visual establecido:
 * - Fondo: #f7f9fb (mismo que Problema)
 * - Colores: #0b1220, #6b7280, #0b63c6, border rgba(0,0,0,0.08)
 * - Cards: rounded-lg, border sutil, sin gradientes recargados
 * - Calculadora: card limpia, slider personalizado, resultados en tabla
 * - Comparación: two-column, tipografía compacta, sin emojis
 * - Todos los var(--color-*) reemplazados por valores directos
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  TrendingDown,
  AlertTriangle,
  DollarSign,
  Truck,
  XCircle,
  Calculator,
  ArrowRight,
  X,
  Check,
} from 'lucide-react';

// ─── Intersection Observer reutilizable ──────────────────────────────────────
const useReveal = (delay = 0) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    el.style.transition = `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
};

// ─── ConsequenceCard ──────────────────────────────────────────────────────────
const ConsequenceCard = ({ icon, titulo, descripcion, variant, delay }) => {
  const ref = useReveal(delay);

  const styles = {
    danger: {
      bar: 'bg-red-400',
      iconBg: 'bg-red-50',
      iconColor: 'text-red-500',
    },
    warning: {
      bar: 'bg-amber-400',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
    },
  };

  const s = styles[variant];

  return (
    <div
      ref={ref}
      className="relative flex flex-col gap-3 bg-white rounded-lg p-5 border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)] overflow-hidden"
    >
      {/* Barra lateral de color */}
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${s.bar}`} />

      {/* Icono */}
      <div className={`w-10 h-10 rounded-full ${s.iconBg} flex items-center justify-center shrink-0 ml-2`}>
        {React.cloneElement(icon, { size: 18, strokeWidth: 1.75, className: s.iconColor })}
      </div>

      {/* Texto */}
      <div className="ml-2">
        <h3 className="text-[15px] font-semibold text-[#0b1220] leading-snug mb-1.5">
          {titulo}
        </h3>
        <p className="text-[13.5px] text-[#6b7280] leading-[1.65]">
          {descripcion}
        </p>
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const Consecuencias = () => {
  const [numCamiones, setNumCamiones] = useState(5);

  const perdidaMensual  = numCamiones * 800;
  const perdida3Meses   = perdidaMensual * 3;
  const perdida6Meses   = perdidaMensual * 6;
  const perdida12Meses  = perdidaMensual * 12;

  const fmt = (n) => `S/ ${n.toLocaleString('es-PE')}`;

  const headerRef     = useReveal(0);
  const calcRef       = useReveal(0);
  const comparaRef    = useReveal(0);

  const scrollToForm = () => {
    document.getElementById('formulario-contacto')?.scrollIntoView({ behavior: 'smooth' });
  };

  const consecuencias = [
    {
      icon: <DollarSign />,
      titulo: 'Pérdidas ocultas que se acumulan',
      descripcion:
        'S/ 300 perdidos aquí, S/ 500 allá. Viajes que parecían rentables pero terminan en rojo. Al final del año, son decenas de miles de soles que se esfumaron.',
      variant: 'danger',
    },
    {
      icon: <XCircle />,
      titulo: 'Clientes que te hacen perder dinero',
      descripcion:
        'Sigues trabajando con ese cliente "grande" que paga bien, pero después de costos reales pierdes en cada viaje. Y rechazas otros clientes rentables por falta de capacidad.',
      variant: 'danger',
    },
    {
      icon: <Truck />,
      titulo: 'Decisiones caras sin información',
      descripcion:
        '¿Comprar otro camión? ¿Contratar más choferes? Sin saber tu rentabilidad actual, puedes estar invirtiendo en crecer... tus pérdidas.',
      variant: 'warning',
    },
    {
      icon: <AlertTriangle />,
      titulo: 'Problemas que detectas tarde',
      descripcion:
        'El camión que consume más combustible, el chofer con rutas ineficientes, el cliente con "extras" no facturados. Los descubres meses después.',
      variant: 'warning',
    },
  ];

  // Items de comparación
  const sinControl = [
    `${fmt(perdidaMensual)} perdidos cada mes`,
    `${fmt(perdida12Meses)} perdidos al año`,
    'Decisiones basadas en intuición',
    'Problemas detectados tarde',
  ];

  const conControl = [
    'Detectas pérdidas en tiempo real',
    'Recuperas S/ 600–1,000 por camión/mes',
    'Decides con datos concretos',
    'Inviertes en lo que sí da resultado',
  ];

  return (
    <section
      id="consecuencias"
      className="w-full py-24 lg:py-28"
      style={{ backgroundColor: '#ffffff' }}
    >
      <div className="max-w-[1120px] mx-auto px-6 flex flex-col gap-12">

        {/* ── Header ── */}
        <header
          ref={headerRef}
          className="max-w-[720px] flex flex-col gap-4"
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-200 w-fit">
            <TrendingDown size={14} strokeWidth={2} className="text-red-500" />
            <span className="text-[13px] font-medium text-red-600">Costo de no actuar</span>
          </div>

          <h2 className="text-[28px] font-bold text-[#0b1220] leading-[1.3] tracking-[-0.01em]">
            Cada mes sin control de rentabilidad te cuesta{' '}
            <span className="text-red-500">dinero real</span>
          </h2>

          <p className="text-[15px] text-[#6b7280] leading-[1.6] max-w-[640px]">
            No se trata solo de "no ganar". Se trata de{' '}
            <strong className="text-[#0b1220] font-semibold">perder activamente</strong>{' '}
            sin darte cuenta. Y las pérdidas se acumulan mes tras mes.
          </p>
        </header>

        {/* ── Grid de consecuencias ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {consecuencias.map((item, i) => (
            <ConsequenceCard key={i} {...item} delay={i * 60} />
          ))}
        </div>

        {/* ── Calculadora ── */}
        <div
          ref={calcRef}
          className="bg-white rounded-xl border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.05)] overflow-hidden"
        >
          {/* Header calculadora */}
          <div className="flex items-center gap-4 px-6 py-5 bg-[#0b1220] border-b border-white/10">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
              <Calculator size={18} strokeWidth={1.75} className="text-white" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-white leading-tight">
                Calcula cuánto podrías estar perdiendo
              </h3>
              <p className="text-[13px] text-white/60 mt-0.5">
                Basado en datos reales del sector de transporte en Perú
              </p>
            </div>
          </div>

          {/* Body calculadora */}
          <div className="px-6 py-6 flex flex-col gap-6">

            {/* Slider */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="camiones"
                  className="text-[14px] font-semibold text-[#0b1220]"
                >
                  ¿Cuántos vehículos tienes en operación?
                </label>
                <span className="text-[15px] font-bold text-[#0b63c6]">
                  {numCamiones} {numCamiones === 1 ? 'camión' : 'camiones'}
                </span>
              </div>

              {/* Slider con estilos inline para cross-browser */}
              <div className="relative">
                <input
                  type="range"
                  id="camiones"
                  min="2"
                  max="10"
                  value={numCamiones}
                  onChange={(e) => setNumCamiones(parseInt(e.target.value))}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #0b63c6 0%, #0b63c6 ${((numCamiones - 2) / 8) * 100}%, #e5e7eb ${((numCamiones - 2) / 8) * 100}%, #e5e7eb 100%)`,
                    outline: 'none',
                    WebkitAppearance: 'none',
                  }}
                />
                {/* Thumb override via global style en App.css */}
              </div>

              {/* Marcas del slider */}
              <div className="flex justify-between px-0.5">
                {[2, 4, 6, 8, 10].map((n) => (
                  <span key={n} className="text-[11px] text-[#6b7280]">{n}</span>
                ))}
              </div>
            </div>

            {/* Resultados */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* 3 meses */}
              <div className="flex flex-col gap-1 px-4 py-3.5 rounded-lg bg-[#f7f9fb] border border-black/[0.06]">
                <span className="text-[11px] font-medium text-[#6b7280] uppercase tracking-wider">
                  En 3 meses
                </span>
                <span className="text-[20px] font-bold text-red-500 leading-tight">
                  -{fmt(perdida3Meses)}
                </span>
              </div>

              {/* 6 meses */}
              <div className="flex flex-col gap-1 px-4 py-3.5 rounded-lg bg-[#f7f9fb] border border-black/[0.06]">
                <span className="text-[11px] font-medium text-[#6b7280] uppercase tracking-wider">
                  En 6 meses
                </span>
                <span className="text-[20px] font-bold text-red-500 leading-tight">
                  -{fmt(perdida6Meses)}
                </span>
              </div>

              {/* 12 meses — destacado */}
              <div className="flex flex-col gap-1 px-4 py-3.5 rounded-lg bg-red-50 border border-red-200">
                <span className="text-[11px] font-medium text-red-400 uppercase tracking-wider">
                  En 1 año
                </span>
                <span className="text-[22px] font-bold text-red-600 leading-tight">
                  -{fmt(perdida12Meses)}
                </span>
                <span className="text-[11px] text-red-400 font-medium">
                  en pérdidas ocultas
                </span>
              </div>
            </div>

            {/* Nota */}
            <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle size={15} strokeWidth={2} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-[13px] text-[#6b7280] leading-[1.6]">
                <strong className="text-[#0b1220] font-semibold">Estimación conservadora:</strong>{' '}
                S/ 800 de pérdida mensual por vehículo debido a viajes no rentables,
                costos no controlados, ineficiencias operativas y clientes que generan pérdidas.
              </p>
            </div>
          </div>
        </div>

        {/* ── Comparación Sin vs Con ── */}
        <div
          ref={comparaRef}
          className="bg-white rounded-xl border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)] overflow-hidden"
        >
          {/* Columnas */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr]">

            {/* Sin control */}
            <div className="flex flex-col gap-3 p-6 bg-red-50/60 border-b sm:border-b-0 sm:border-r border-black/[0.06]">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                  <X size={12} strokeWidth={2.5} className="text-red-500" />
                </div>
                <h4 className="text-[14px] font-semibold text-[#0b1220]">Sin control</h4>
              </div>
              <ul className="flex flex-col gap-2">
                {sinControl.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <X size={13} strokeWidth={2} className="text-red-400 shrink-0 mt-0.5" />
                    <span className="text-[13.5px] text-[#6b7280] leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* VS */}
            <div className="flex items-center justify-center px-4 py-4 sm:py-0">
              <span className="text-[12px] font-bold text-[#6b7280] tracking-widest uppercase">
                VS
              </span>
            </div>

            {/* Con control */}
            <div className="flex flex-col gap-3 p-6 bg-emerald-50/60">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check size={12} strokeWidth={2.5} className="text-emerald-600" />
                </div>
                <h4 className="text-[14px] font-semibold text-[#0b1220]">Con control de rentabilidad</h4>
              </div>
              <ul className="flex flex-col gap-2">
                {conControl.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={13} strokeWidth={2} className="text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-[13.5px] text-[#6b7280] leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-5 bg-[#f7f9fb] border-t border-black/[0.06]">
            <p className="text-[14px] text-[#6b7280] leading-[1.6] max-w-md">
              La inversión en control se paga sola en{' '}
              <strong className="text-[#0b1220] font-semibold">el primer mes</strong>{' '}
              al evitar viajes no rentables.
            </p>
            <button
              onClick={scrollToForm}
              className="flex items-center gap-2 bg-[#0b63c6] hover:bg-[#0957b0] text-white text-[14px] font-semibold px-5 py-2.5 rounded-lg transition-colors duration-200 whitespace-nowrap shrink-0"
            >
              Agendar Diagnóstico
              <ArrowRight size={15} strokeWidth={2} />
            </button>
          </div>
        </div>

      </div>

      {/* Slider thumb styles — inyectados una vez */}
      <style>{`
        #camiones::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #0b63c6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 0 0 1px #0b63c6, 0 2px 6px rgba(11,99,198,0.3);
          transition: transform 0.15s ease;
        }
        #camiones::-webkit-slider-thumb:hover {
          transform: scale(1.15);
        }
        #camiones::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #0b63c6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 0 0 1px #0b63c6, 0 2px 6px rgba(11,99,198,0.3);
        }
      `}</style>
    </section>
  );
};

export default Consecuencias;